import { JiraIssueAri } from '@atlassian/ari/jira/issue';
import { statusCategories } from '@atlassian/jira-common-constants/src/index.tsx';
import { gqlTagGira } from '@atlassian/jira-issue-fetch-services-common/src/services/issue-gira-data/gira-fragments/index.tsx';
import {
	type GiraQuery,
	getClient,
} from '@atlassian/jira-issue-fetch-services/src/common/utils/client.tsx';

import type {
	ParentIssueDetailsQuery,
	ParentIssueDetailsQueryVariables,
	ParentIssueDetailsQuery_jira_issueById_fieldsById_edges_node as FieldEdgeNode,
	ParentIssueDetailsQuery_jira_issueById_fieldsById_edges_node_JiraIssueTypeField as JiraIssueTypeFieldNode,
	ParentIssueDetailsQuery_jira_issueById_fieldsById_edges_node_JiraSingleLineTextField as JiraSingleLineTextFieldNode,
	ParentIssueDetailsQuery_jira_issueById_fieldsById_edges_node_JiraStatusField as JiraStatusFieldNode,
} from './__generated_apollo__/ParentIssueDetailsQuery';
import { CAUSE_FETCH_PARENT_FIELD_DETAILS_REQUEST_FAILED } from './constants.tsx';

export const isJiraIssueTypeFieldNode = (
	node: FieldEdgeNode | null | undefined,
): node is JiraIssueTypeFieldNode => node?.__typename === 'JiraIssueTypeField';

export const isJiraSingleLineTextFieldNode = (
	node: FieldEdgeNode | null | undefined,
): node is JiraSingleLineTextFieldNode => node?.__typename === 'JiraSingleLineTextField';

export const isJiraStatusFieldNode = (
	node: FieldEdgeNode | null | undefined,
): node is JiraStatusFieldNode => node?.__typename === 'JiraStatusField';

// To update the parent issue details query types, run the following commands from the playbook:
// https://hello.atlassian.net/wiki/spaces/JFP/pages/1889693782/Jira+Playbook+Graphql
// yarn dev graphql:fetch
// yarn update-gira
// yarn dev graphql:generate

export const getParentIssueDetailsQuery: GiraQuery<ParentIssueDetailsQuery> = gqlTagGira`
  query ParentIssueDetailsQuery($ari: ID!) {
    jira {
      issueById(id: $ari) {
        issueId
        key
        issueColorField {
          color {
            colorKey
          }
        }
        fieldsById(ids: ["summary", "issuetype", "status"]) {
          edges {
            node {
              __typename
              ... on JiraIssueTypeField {
                fieldId
                issueType {
                  issueTypeId
                  name
                  avatar {
                    xsmall
                  }
                }
              }
              ... on JiraSingleLineTextField {
                fieldId
                text
              }
              ... on JiraStatusField {
                fieldId
                status {
                  statusCategory {
                    statusCategoryId
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export async function fetchParentIssueDetails(siteId: string, issueId: string) {
	try {
		const variables: ParentIssueDetailsQueryVariables = {
			ari: JiraIssueAri.create({
				siteId,
				issueId,
			}).toString(),
		};

		const { data } = await getClient('').query<{
			data: ParentIssueDetailsQuery;
		}>({
			query: getParentIssueDetailsQuery,
			variables,
			operationName: 'ParentIssueDetailsQuery',
		});

		return data;
	} catch (error) {
		throw new Error(`Error fetching data for parent issue with issueId "${issueId}"`, {
			cause: CAUSE_FETCH_PARENT_FIELD_DETAILS_REQUEST_FAILED,
		});
	}
}

export const checkParentIssueIsDone = (node: JiraStatusFieldNode): boolean => {
	const doneStatusCategoryId =
		statusCategories.StatusCategoryIds[statusCategories.StatusCategoryTypes.DONE];
	return (
		doneStatusCategoryId &&
		String(doneStatusCategoryId) === node.status?.statusCategory?.statusCategoryId
	);
};
